import {createStyles, Button as MantineButton} from '@mantine/core';
import * as React from 'react';
import {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ACTION_CATEGORY, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import CreateWorklistWithCollaboratorsModal from 'worklists/CreateWorklistWithCollaboratorsModal';
import {renderWorklistTypeOptions} from 'worklists/renderWorklistTypeOptions';
import {RefreshOptions} from 'worklists/WorklistActions';
import {WorklistType, worklistTypeDisplayName} from 'worklists/WorklistConstants';

export type WorklistCreateButtonProps = {
  className?: string;
  refreshOptions?: RefreshOptions;
} & ({buttonStyle: ButtonStyle} | {newButton: true});

const useStyles = createStyles((theme) => ({
  xsMarginRight: {
    marginRight: theme.spacing.xs,
  },
  dropdown: {
    marginTop: 1,
  },
}));

export default function WorklistCreateButton(props: WorklistCreateButtonProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const {classes} = useStyles();

  const analyticsModule = ANALYTICS_MODULE.WORKLISTS;

  const openCreateWorklistModal = (selectedType: WorklistType): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, analyticsModule, {
      modalType: 'create_worklist',
      worklistType: selectedType,
    });
    legacyRunModal(CreateWorklistWithCollaboratorsModal, {
      refreshOptions: props.refreshOptions,
      worklistType: selectedType,
    });
  };
  const getCreateWorklistOptionText = (worklistType: WorklistType): string =>
    `Create ${worklistTypeDisplayName(worklistType)} worklist`;

  const buttonOnClick = (): void => {
    analytics.trackEventWithModule(
      {
        module: analyticsModule,
        action: 'clicked_create_worklist_button',
        actionCategory: ACTION_CATEGORY.READ,
      },
      analyticsModule,
      {}
    );
    setIsOpen(true);
  };

  return (
    <Dropdown
      className={classes.dropdown}
      isOpen={isOpen}
      onToggle={(newIsOpen) => {
        setIsOpen(newIsOpen);
      }}
      menu={
        <Menu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.LARGE}
          onMouseLeave={() => {
            setIsOpen(false);
          }}
          dropLeft={true}
        >
          {renderWorklistTypeOptions({
            onSelectWorklistType: openCreateWorklistModal,
            getOptionText: getCreateWorklistOptionText,
          })}
        </Menu>
      }
    >
      {
        // eslint-disable-next-line benchlint/no-in-operator -- discriminant for old vs new button
        'newButton' in props ? (
          <MantineButton
            variant="default"
            size="md"
            onClick={buttonOnClick}
            className={props.className}
            leftIcon={<BenchlingIcon icon="compose" className={classes.xsMarginRight} />}
          >
            New
          </MantineButton>
        ) : (
          <Tooltip title="New worklist" placement={TooltipPlacement.BOTTOM} container="body" disabled={false}>
            <Button
              onClick={buttonOnClick}
              aria-label="Create worklist"
              buttonStyle={props.buttonStyle}
              className={props.className}
              size={ButtonSize.SMALL}
              icon="compose"
              dataTestAttrs={{component: 'WorklistCreateButton', element: 'button'}}
            />
          </Tooltip>
        )
      }
    </Dropdown>
  );
}
